// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the Omana People surface
//  ---------------------------------------------------------------------------
//  Writing somebody's name down is how they get into ManDana at all. This is the
//  three-screen ceremony that does it: the form, the username it mints, and
//  handing that username over.
//
//  IT IS A CARD ON THE MIDDLE OF THE SCREEN, NOT A DROPDOWN, and the precedent
//  is deliberate: this is the same shape RecoveryCodeDialog takes, because it is
//  the same kind of moment — a credential that exists once and has to be passed
//  to a person. A 214px anchored menu is the app's grammar for changing a
//  setting. It is not its grammar for "here is the address somebody will carry
//  for the rest of their time in this app".
//
//  THE MESSAGE IS ALWAYS ON SCREEN, not only inside the share sheet. The system
//  share sheet is not available in every browser, the clipboard is refused in
//  some, and a person who cannot reach either must still be able to read the
//  words and type them into whatever they actually use. So the text is rendered,
//  selectable, every time — and Share is an accelerator over it rather than the
//  only door.
//
//  NOTHING IS DRAWN OPTIMISTICALLY. The username comes back from the server or
//  the panel says what happened and stays on the form with the names still in
//  it. There is no local guess at the number: it is a counter over everybody
//  with that name, and a guess would be an address that belongs to somebody
//  else. See app/md-omana.ts and migrations/0007_omana_registry.sql.
//
//  A CARD IN THE GALLERY OPENS THIS, not a row page. An Omana row is a
//  projection of a table on the server with no home document behind it, so a
//  row page would open onto nothing; what somebody actually wants from a card is
//  the invitation again. md-database.jsx intercepts the open for this one
//  database and hands the person's id here.
//
//  AND A CARD THAT HAS ARRIVED CAN ALSO RESCUE. Somebody who has lost every
//  device they signed in on has no passkey, no linked screen and no recovery
//  code — what they have left is whoever wrote their name down. So an ACTIVATED
//  card offers a one-time sign-in code for that account, which is a credential
//  to somebody else's data and is described as exactly that, above the button
//  rather than under the code. A card nobody has taken up does not offer it:
//  there is no account behind an invitation, and what that person needs is the
//  invitation again, which is already on the screen.
//
//  Exposes <MdOmanaSheet>, mdOmanaShareText and mdOmanaShare.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useStO, useEffect: useEfO, useRef: useRfO } = React;
const { Glass: GlassO, ink: inkO, surf: surfO, mdRgba: rgbaO, mdIsLight: isLightO } = window;

/* The invitation, exactly as it goes out. Held in one function because it is
   the only place these words exist: a message assembled at the call site is a
   message that is different in the second place it is assembled from. */
function mdOmanaShareText(person) {
  if (!person || typeof person !== 'object') return '';
  const first = String(person.first || '').trim();
  return `Sallamu Alaykom ${first},\n\n`
    + `I've Added you to my Omana Network on www.ManDana.App.\n\n`
    + `Your username is ${person.username}\n\n`
    + 'See you there 💧';
}

/* Hand it over, by whichever door this browser actually opens. Three answers
   rather than two, because "the share sheet is not here" and "nothing worked"
   ask different things of the person: the first is silent and fine, the second
   has to say so. The text is on screen either way, so `manual` is a working
   state and not a failure. */
async function mdOmanaShare(text) {
  const words = String(text || '');
  if (!words) return 'manual';
  try {
    if (navigator.share) { await navigator.share({ text: words }); return 'shared'; }
  } catch (e) {
    /* A dismissed share sheet lands here, and it is not an error — the person
       changed their mind. Falling through to the clipboard would be the app
       insisting. */
    return 'manual';
  }
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(words);
      return 'copied';
    }
  } catch (e) {}
  return 'manual';
}

/* What each refusal means to somebody standing in front of the form, rather
   than what the server called it. `unreachable` is the one that has to say the
   most: it is not a mistake the person made, and there is nothing to fix by
   trying a different name. */
const OMANA_REFUSAL = {
  unreachable: 'No answer from the server. A username is minted there, so nobody can be written down while this device is offline.',
  not_signed_in: 'Your session has ended. Sign in again — somebody has to be vouching for them.',
  failed: 'That did not go through, and nobody was added.',
  // The same non-answer the front door names — see md-landing.jsx.
  malformed: 'The server answered something this app could not read, and nobody was added. Try again in a moment.',
};
function omanaRefusalWords(error, field) {
  if (error === 'bad_person') {
    if (field === 'gender') return 'Choose Female or Male. Whoever adds a person answers this, so they are never asked on their way in.';
    return 'That name cannot be part of a username. Letters and numbers only — no dots, and no @.';
  }
  return OMANA_REFUSAL[error] || OMANA_REFUSAL.failed;
}

/* And what each refusal of a SIGN-IN CODE means, which is a different set of
   sentences from the ones above: nobody was being written down, so "nobody was
   added" would describe an act that was never attempted. `not_activated` is the
   one worth spelling out — it is not a mistake, it is the person on the card
   never having arrived, and the answer to it is the invitation that is already
   on screen right above it. */
const OMANA_CODE_REFUSAL = {
  not_activated: 'They have not signed in with this username yet, so there is no account to sign in to. Send them the invitation above instead.',
  unknown_person: 'This person is not on your network, so no code can be minted for them.',
  not_signed_in: 'Your session has ended. Sign in again — only the person who vouched for them can do this.',
  unreachable: 'No answer from the server. A code is minted there, so none can be made while this device is offline.',
  malformed: 'The server answered something this app could not read, and no code was made. Try again in a moment.',
  failed: 'That did not go through, and no code was made.',
};
const omanaCodeRefusalWords = (error) => OMANA_CODE_REFUSAL[error] || OMANA_CODE_REFUSAL.failed;

/** The two words the roster's own status is written in. */
const omanaStatusWords = (status) => status === 'activated' ? 'Activated' : 'Not activated yet';

function MdOmanaSheet({ accent = '#7C68E6', mode = 'add', personId = null, close, onAdded = null }) {
  const light = isLightO();
  const [first, setFirst] = useStO('');
  const [last, setLast] = useStO('');
  const [gender, setGender] = useStO('');
  const [busy, setBusy] = useStO(false);
  const [refused, setRefused] = useStO(null);
  const [made, setMade] = useStO(null);
  const [handed, setHanded] = useStO(null);
  const [code, setCode] = useStO(null);
  const [codeBusy, setCodeBusy] = useStO(false);
  const [codeRefused, setCodeRefused] = useStO(null);
  const [codeHanded, setCodeHanded] = useStO(null);
  const firstRef = useRfO(null);

  /* Escape and browser Back put this away, through the one stack every surface
     in this app shares. Unlike the recovery code there is nothing here that
     cannot be seen again: the card is still in the gallery, and opening it
     shows this same message. */
  window.useMdDismissLayer(() => close(), true, null, 'confirm');

  useEfO(() => { if (firstRef.current) firstRef.current.focus(); }, []);

  /* Read at render, so a roster that refreshes underneath this panel updates
     the status line the person is looking at. */
  const shown = made || (mode === 'share' && window.mdOmanaPerson ? window.mdOmanaPerson(personId) : null);
  const text = shown ? mdOmanaShareText(shown) : '';

  const submit = async () => {
    if (busy) return;
    setRefused(null);
    setBusy(true);
    const res = await window.mdOmanaAddPerson({ first, last, gender });
    setBusy(false);
    if (!res.ok) { setRefused({ error: res.error, field: res.field }); return; }
    setMade(res.person);
    setHanded(null);
    if (onAdded) onAdded(res.person);
  };

  const again = () => { setMade(null); setFirst(''); setLast(''); setGender(''); setHanded(null);
    setCode(null); setCodeRefused(null); setCodeHanded(null);
    if (firstRef.current) firstRef.current.focus(); };

  const mintCode = async () => {
    if (codeBusy || !shown) return;
    setCodeRefused(null);
    setCodeBusy(true);
    const res = await window.mdOmanaRecoveryCode(shown.id);
    setCodeBusy(false);
    if (!res.ok) { setCodeRefused(res.error || 'failed'); return; }
    setCode(res.code);
    setCodeHanded(null);
  };

  const ready = first.trim() && last.trim() && gender && !busy;

  const label = { display: 'block', color: inkO(0.42), fontSize: 10.5, fontWeight: 700,
    letterSpacing: '.09em', textTransform: 'uppercase', margin: '0 0 6px' };
  const field = { width: '100%', height: 40, borderRadius: 11, outline: 0, padding: '0 12px',
    background: surfO(0.14), border: `1px solid ${inkO(0.14)}`, color: inkO(0.95),
    fontSize: 15, fontWeight: 550 };
  const primary = (on) => ({ width: '100%', marginTop: 18, padding: '12px 14px', borderRadius: 12,
    cursor: on ? 'pointer' : 'default', border: 0, background: on ? rgbaO(accent, 1) : inkO(0.1),
    color: on ? '#fff' : inkO(0.32), fontSize: 14.5, fontWeight: 700, letterSpacing: '.01em' });
  const quiet = { flex: 1, padding: '10px', borderRadius: 11, cursor: 'pointer',
    background: 'transparent', border: `1px solid ${inkO(0.14)}`, color: inkO(0.66),
    fontSize: 13.5, fontWeight: 600 };
  const plane = window.mdSceneRegistry?.().confirm?.plane ?? window.MD_SHEET_ASK_Z;

  const pill = (value, word) => {
    const on = gender === value;
    return <button type="button" role="radio" aria-checked={on} key={value}
      onClick={() => setGender(value)}
      style={{ flex: 1, padding: '10px', borderRadius: 11, cursor: 'pointer',
        background: on ? rgbaO(accent, 0.18) : 'transparent',
        border: `1px solid ${on ? rgbaO(accent, 0.55) : inkO(0.14)}`,
        color: on ? inkO(0.95) : inkO(0.6), fontSize: 13.5, fontWeight: 640 }}>{word}</button>;
  };

  /* The message, and the one control that hands it over. Shared by both the
     confirmation and a card reopened later, because they are the same act. */
  const invitation = <React.Fragment>
    <div style={{ marginTop: 16 }}>
      <span style={label}>The message</span>
      <div data-md-omana-message style={{ padding: '12px 13px', borderRadius: 12,
        background: surfO(0.1), border: `1px solid ${inkO(0.1)}`, color: inkO(0.72),
        fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', userSelect: 'text' }}>{text}</div>
    </div>
    <button type="button" style={primary(true)}
      onClick={async () => setHanded(await mdOmanaShare(text))}>
      {handed === 'shared' ? 'Shared' : handed === 'copied' ? 'Copied' : 'Share the invitation'}
    </button>
    {handed === 'manual' && <p style={{ margin: '9px 0 0', color: inkO(0.5), fontSize: 12.5, lineHeight: 1.5 }}>
      This browser did not open a share sheet. The message above is selectable — copy it from there.
    </p>}
  </React.Fragment>;

  /* The last way back in, and only for somebody who has an account to get back
     into. What it grants is written above the button rather than under the code:
     a verifier about to paste this into the wrong chat window needs the sentence
     while their finger is still over the control. */
  const rescue = shown && shown.status === 'activated' && <React.Fragment>
    <div style={{ height: 1, background: inkO(0.1), margin: '20px 0 16px' }}/>
    <span style={label}>If they cannot get in</span>
    <p style={{ margin: '0 0 12px', color: inkO(0.58), fontSize: 12.5, lineHeight: 1.55 }}>
      Anyone holding this code can sign in as {shown.first}, with everything in their account.
      Give it to them and to nobody else.
    </p>

    {!code && <button type="button" disabled={codeBusy} style={{ ...quiet, width: '100%', flex: 'none',
      cursor: codeBusy ? 'default' : 'pointer' }} onClick={mintCode}>
      {codeBusy ? 'Generating…' : 'Generate a sign-in code'}
    </button>}

    {code && <React.Fragment>
      <div data-md-omana-code style={{ padding: '13px 12px', borderRadius: 12,
        textAlign: 'center', background: surfO(0.14),
        border: `1px solid ${rgbaO(accent, 0.4)}`, color: inkO(0.95),
        fontSize: 16.5, fontWeight: 680, letterSpacing: '.1em', wordBreak: 'break-all',
        userSelect: 'text',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{code}</div>
      <p style={{ margin: '10px 0 0', color: inkO(0.5), fontSize: 12.5, lineHeight: 1.55 }}>
        Valid for 10 minutes, and it works once. They type it into Sign in on their device.
      </p>
      <button type="button" style={{ ...quiet, width: '100%', flex: 'none', marginTop: 11 }}
        onClick={async () => setCodeHanded(await mdOmanaShare(code))}>
        {codeHanded === 'shared' ? 'Shared' : codeHanded === 'copied' ? 'Copied' : 'Copy the code'}
      </button>
    </React.Fragment>}

    {codeRefused && <p data-md-omana-code-refusal role="status" style={{ margin: '12px 0 0',
      color: inkO(0.72), fontSize: 12.5, lineHeight: 1.55 }}>
      {omanaCodeRefusalWords(codeRefused)}
    </p>}
  </React.Fragment>;

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: plane, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
      background: light ? 'rgba(239,241,244,0.9)' : 'rgba(4,6,12,0.84)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      animation: 'mdConfIn .2s ease' }}
      onPointerDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <GlassO radius={22} alpha={0.12} style={{ padding: 22, width: 'min(420px, 100%)',
        maxHeight: '100%', overflow: 'auto',
        animation: 'mdConfPop .26s cubic-bezier(.22,1,.36,1)' }}>
        <div data-md-omana-sheet={shown ? 'invitation' : 'form'} role="dialog" aria-modal="true"
          aria-label={shown ? 'The invitation' : 'Add someone to your network'}>

          {!shown && <React.Fragment>
            <div style={{ color: inkO(0.95), fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              Add someone to your network
            </div>
            <p style={{ color: inkO(0.58), fontSize: 13.5, lineHeight: 1.55, margin: '0 0 18px' }}>
              Their username is minted the moment you write their name down. Nobody can make a
              ManDana account without one.
            </p>

            <div style={{ marginBottom: 12 }}>
              <span style={label} id="md-omana-first">First Name</span>
              <input ref={firstRef} style={field} value={first} aria-labelledby="md-omana-first"
                autoComplete="off" data-lpignore="true" data-1p-ignore="true" dir="auto"
                onChange={(event) => setFirst(event.target.value)}/>
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={label} id="md-omana-last">Last Name</span>
              <input style={field} value={last} aria-labelledby="md-omana-last"
                autoComplete="off" data-lpignore="true" data-1p-ignore="true" dir="auto"
                onChange={(event) => setLast(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter' && ready) submit(); }}/>
            </div>
            <div>
              <span style={label} id="md-omana-gender">Gender</span>
              <div role="radiogroup" aria-labelledby="md-omana-gender" style={{ display: 'flex', gap: 8 }}>
                {pill('female', 'Female')}
                {pill('male', 'Male')}
              </div>
            </div>

            {refused && <p data-md-omana-refusal role="status" style={{ margin: '14px 0 0',
              color: inkO(0.72), fontSize: 12.5, lineHeight: 1.55 }}>
              {omanaRefusalWords(refused.error, refused.field)}
            </p>}

            <button type="button" disabled={!ready} style={primary(!!ready)} onClick={submit}>
              {busy ? 'Adding…' : 'Add to my network'}
            </button>
            <button type="button" style={{ ...quiet, width: '100%', flex: 'none', marginTop: 9 }}
              onClick={close}>Cancel</button>
          </React.Fragment>}

          {shown && <React.Fragment>
            <div style={{ color: inkO(0.95), fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              {made ? `${shown.first} is on your network` : `${shown.first} ${shown.last}`.trim()}
            </div>
            <div data-md-omana-username style={{ padding: '13px 12px', borderRadius: 12,
              textAlign: 'center', background: surfO(0.14),
              border: `1px solid ${rgbaO(accent, 0.4)}`, color: inkO(0.95),
              fontSize: 15.5, fontWeight: 680, wordBreak: 'break-all',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {shown.username}
            </div>
            <p data-md-omana-status style={{ margin: '10px 0 0', color: inkO(0.5),
              fontSize: 12.5, lineHeight: 1.55 }}>
              {shown.status === 'activated'
                ? 'Activated — they have signed in with it.'
                : 'Not activated until they sign in with it.'}
            </p>
            {invitation}
            {rescue}
            <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
              {made && <button type="button" style={quiet} onClick={again}>Add another</button>}
              <button type="button" style={quiet} onClick={close}>Done</button>
            </div>
          </React.Fragment>}
        </div>
      </GlassO>
    </div>,
    document.body,
  );
}

Object.assign(window, { MdOmanaSheet, mdOmanaShareText, mdOmanaShare, omanaStatusWords,
  omanaCodeRefusalWords });
